import type { PhotographerDetails } from './types';
import type { PhotoVogueService } from './service.interface';
import type {
  BatchResult,
  DailyAssignment,
  Photo,
  PhotoBatchPatch,
  Photographer,
  PhotoFilters,
  PhotoVogueFilterOptions,
  PhotographerFilters,
  PhotographerQueryParams,
  PhotographersResponse,
  PhotographerSort,
  PhotoModerationPatch,
  PhotoPatch,
  PhotoQueryParams,
  PhotosResponse,
  PhotoSort,
} from './types';
import { photosMock } from './mocks/photos.mock';
import { photographersMock } from './mocks/photographers.mock';

const delay = (ms = 220) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const includesInsensitive = (haystack: string, needle: string) =>
  haystack.toLowerCase().includes(needle.toLowerCase());

const toDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const matchPhoto = (photo: Photo, filters: PhotoFilters): boolean => {
  if (filters.states?.length && !filters.states.includes(photo.state))
    return false;
  if (filters.category && photo.category !== filters.category) return false;
  if (filters.type && !photo.types.includes(filters.type)) return false;
  if (filters.excludeType && photo.types.includes(filters.excludeType))
    return false;
  if (
    filters.themes?.length &&
    !filters.themes.some((theme) => photo.themes.includes(theme))
  )
    return false;
  if (
    filters.tags?.length &&
    !filters.tags.some((tag) => photo.tags.includes(tag))
  )
    return false;
  if (
    filters.photographerId &&
    photo.photographer.id !== filters.photographerId
  )
    return false;
  if (filters.uploadedFrom || filters.uploadedTo) {
    const uploadedDay = photo.createdAt.slice(0, 10);
    if (filters.uploadedFrom && uploadedDay < filters.uploadedFrom)
      return false;
    if (filters.uploadedTo && uploadedDay > filters.uploadedTo) return false;
  }
  if (filters.search?.trim()) {
    const haystack = `${photo.title} ${photo.description} ${photo.photographer.name} ${photo.tags.join(' ')}`;
    if (!includesInsensitive(haystack, filters.search.trim())) return false;
  }
  return true;
};

const comparePhotos = (a: Photo, b: Photo, sort: PhotoSort): number => {
  const av = String(a[sort.field] ?? '');
  const bv = String(b[sort.field] ?? '');
  const cmp = av.localeCompare(bv);
  return sort.direction === 'asc' ? cmp : -cmp;
};

const matchPhotographer = (
  photographer: Photographer,
  filters: PhotographerFilters,
): boolean => {
  if (filters.states?.length && !filters.states.includes(photographer.state))
    return false;
  if (filters.tier && photographer.tier !== filters.tier) return false;
  if (filters.country && photographer.country !== filters.country) return false;
  if (filters.withPhotosOnly && photographer.totalPhotos === 0) return false;
  if (filters.search?.trim()) {
    const haystack = photographer.name;
    if (!includesInsensitive(haystack, filters.search.trim())) return false;
  }
  return true;
};

const comparePhotographers = (
  a: Photographer,
  b: Photographer,
  sort: PhotographerSort,
): number => {
  const cmp =
    sort.field === 'name'
      ? a.name.localeCompare(b.name)
      : a.totalPhotos - b.totalPhotos;
  return sort.direction === 'asc' ? cmp : -cmp;
};

/**
 * In-memory PhotoVogue service for Step 1. Clones the seed data so moderation
 * transitions persist for the session. Same method surface as the future
 * `HttpPhotoVogueService`, so nothing above the service layer changes in Step 2.
 */
export class MockPhotoVogueService implements PhotoVogueService {
  private photos: Photo[] = photosMock.map((photo) => ({ ...photo }));
  private photographers: Photographer[] = photographersMock.map(
    (photographer) => ({ ...photographer }),
  );
  private dailies = new Map<string, string>();

  constructor() {
    // Seed a few upcoming Pic-of-the-Day assignments from approved photos.
    const approved = this.photos.filter((photo) => photo.state === 'approved');
    const today = new Date();
    approved.slice(0, 3).forEach((photo, index) => {
      const date = new Date(today);
      date.setDate(date.getDate() + index);
      this.dailies.set(toDateKey(date), photo.id);
      if (!photo.types.includes('daily')) {
        photo.types = [...photo.types, 'daily'];
      }
    });
  }

  async getFilterValues(
    field: keyof PhotoVogueFilterOptions,
  ): Promise<string[]> {
    return (await this.getFilterOptions())[field];
  }

  async getFilterOptions(): Promise<PhotoVogueFilterOptions> {
    await delay(80);
    return {
      categories: [
        ...new Set(this.photos.map((photo) => photo.category)),
      ].sort(),
      themes: [...new Set(this.photos.flatMap((photo) => photo.themes))].sort(),
      tags: [...new Set(this.photos.flatMap((photo) => photo.tags))].sort(),
      countries: [
        ...new Set(
          this.photographers.map((photographer) => photographer.country),
        ),
      ].sort(),
    };
  }

  async getPhotos(params: PhotoQueryParams): Promise<PhotosResponse> {
    await delay();
    const { filters, sort, pagination } = params;
    const matched = this.photos
      .filter((photo) => matchPhoto(photo, filters))
      .sort((a, b) => comparePhotos(a, b, sort));
    const start = pagination.page * pagination.limit;
    const pageItems = matched.slice(start, start + pagination.limit);
    return {
      data: pageItems.map((photo) => ({ ...photo })),
      _ids: pageItems.map((photo) => photo.id),
      count: matched.length,
    };
  }

  async getPhoto(id: string): Promise<Photo> {
    await delay(140);
    const photo = this.photos.find((item) => item.id === id);
    if (!photo) throw new Error(`Photo ${id} not found`);
    return { ...photo };
  }

  async moderatePhoto(id: string, patch: PhotoModerationPatch): Promise<Photo> {
    await delay(160);
    const photo = this.photos.find((item) => item.id === id);
    if (!photo) throw new Error(`Photo ${id} not found`);
    photo.state = patch.state;
    photo.stateUpdatedAt = new Date().toISOString();
    return { ...photo };
  }

  async patchPhotos(patches: PhotoBatchPatch[]): Promise<BatchResult> {
    await delay(240);
    const now = new Date().toISOString();
    const failed: string[] = [];
    patches.forEach((patch) => {
      const photo = this.photos.find((item) => item.id === patch.id);
      if (!photo) {
        failed.push(patch.id);
        return;
      }
      photo.state = patch.state;
      photo.types = [...patch.types];
      photo.stateUpdatedAt = now;
    });
    return { failed };
  }

  async getPhotographers(
    params: PhotographerQueryParams,
  ): Promise<PhotographersResponse> {
    await delay();
    const { filters, sort, pagination } = params;
    const matched = this.photographers
      .filter((photographer) => matchPhotographer(photographer, filters))
      .sort((a, b) => comparePhotographers(a, b, sort));
    const start = pagination.page * pagination.limit;
    const pageItems = matched.slice(start, start + pagination.limit);
    return {
      data: pageItems.map((photographer) => ({ ...photographer })),
      _ids: pageItems.map((photographer) => photographer.id),
      count: matched.length,
    };
  }

  async getPhotographerDetails(id: string): Promise<PhotographerDetails> {
    const photographer = await this.getPhotographer(id);
    return {
      biography: photographer.biography ?? '',
      websites: photographer.websites ?? [],
      banCount: photographer.banCount,
      categories: ['Portrait'],
      themes: ['People'],
      agents: [],
      email: 'photographer@example.com',
      contact: '+1 2025550100',
      contactStatus: 'available',
    };
  }

  async getPhotographer(id: string): Promise<Photographer> {
    await delay(140);
    const photographer = this.photographers.find((item) => item.id === id);
    if (!photographer) throw new Error(`Photographer ${id} not found`);
    return { ...photographer };
  }

  async deletePhotographer(id: string): Promise<void> {
    await delay();
    const photoIds = new Set(
      this.photos
        .filter((photo) => photo.photographer.id === id)
        .map((photo) => photo.id),
    );
    this.photos = this.photos.filter((photo) => !photoIds.has(photo.id));
    this.photographers = this.photographers.filter(
      (photographer) => photographer.id !== id,
    );
    for (const [date, photoId] of this.dailies) {
      if (photoIds.has(photoId)) this.dailies.delete(date);
    }
  }

  async moderatePhotographer(
    id: string,
    state: Photographer['state'],
  ): Promise<Photographer> {
    await delay(160);
    const photographer = this.photographers.find((item) => item.id === id);
    if (!photographer) throw new Error(`Photographer ${id} not found`);
    photographer.state = state;
    return { ...photographer };
  }

  async updatePhotographerSpotlight(
    id: string,
    spotlight: boolean,
  ): Promise<Photographer> {
    await delay(160);
    const photographer = this.photographers.find((item) => item.id === id);
    if (!photographer) throw new Error(`Photographer ${id} not found`);
    photographer.tier = spotlight ? 'gold' : 'basic';
    return { ...photographer };
  }

  async updatePhoto(id: string, patch: PhotoPatch): Promise<Photo> {
    await delay(160);
    const photo = this.photos.find((item) => item.id === id);
    if (!photo) throw new Error(`Photo ${id} not found`);
    if (patch.title !== undefined) photo.title = patch.title;
    if (patch.description !== undefined) photo.description = patch.description;
    if (patch.category !== undefined) photo.category = patch.category;
    if (patch.tags !== undefined) photo.tags = patch.tags;
    if (patch.themes !== undefined) photo.themes = patch.themes;
    if (patch.types !== undefined) photo.types = patch.types;
    if (patch.state !== undefined && patch.state !== photo.state) {
      photo.state = patch.state;
      photo.stateUpdatedAt = new Date().toISOString();
    }
    return { ...photo };
  }

  async getDailies(
    dateFrom: string,
    dateTo: string,
  ): Promise<DailyAssignment[]> {
    await delay();
    const result: DailyAssignment[] = [];
    this.dailies.forEach((photoId, date) => {
      if (date >= dateFrom && date <= dateTo) {
        const photo = this.photos.find((item) => item.id === photoId);
        if (photo) result.push({ date, photo: { ...photo } });
      }
    });
    return result.sort((a, b) => a.date.localeCompare(b.date));
  }

  async setDaily(date: string, photoId: string): Promise<DailyAssignment> {
    await delay(160);
    const photo = this.photos.find((item) => item.id === photoId);
    if (!photo) throw new Error(`Photo ${photoId} not found`);
    this.dailies.forEach((assignedPhotoId, assignedDate) => {
      if (assignedPhotoId === photoId) this.dailies.delete(assignedDate);
    });
    const replacedPhotoId = this.dailies.get(date);
    this.dailies.set(date, photoId);
    if (replacedPhotoId && replacedPhotoId !== photoId) {
      const replacedPhoto = this.photos.find(
        (item) => item.id === replacedPhotoId,
      );
      if (replacedPhoto) {
        replacedPhoto.types = replacedPhoto.types.filter(
          (type) => type !== 'daily',
        );
      }
    }
    if (!photo.types.includes('daily')) {
      photo.types = [...photo.types, 'daily'];
    }
    return { date, photo: { ...photo } };
  }

  async removeDaily(date: string, expectedPhotoId?: string): Promise<void> {
    await delay(160);
    const photoId = this.dailies.get(date);
    if (expectedPhotoId && photoId !== expectedPhotoId) return;
    this.dailies.delete(date);
    if (photoId && !Array.from(this.dailies.values()).includes(photoId)) {
      const photo = this.photos.find((item) => item.id === photoId);
      if (photo) photo.types = photo.types.filter((type) => type !== 'daily');
    }
  }
}
